import { apiClient } from './client';

/**
 * Data flywheel beacon helpers.
 *
 * Batches product-analytics events and flushes them on a short timer.
 * Everything is fire-and-forget: callers never await. On navigation we
 * flush synchronously via sendBeacon so in-flight events reach the server.
 */

type FlywheelEvent = {
  event_type: string;
  session_id?: string;
  product?: string;
  entity_type?: string;
  entity_id?: string;
  context?: Record<string, unknown>;
  duration_ms?: number;
};

const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER = 50;

let buffer: FlywheelEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let sessionId: string | null = null;

function getSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = sessionStorage.getItem('argos_session_id');
    if (existing) {
      sessionId = existing;
      return existing;
    }
  } catch {
    // sessionStorage may be unavailable (private mode, SSR)
  }
  const fresh =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 32)
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  sessionId = fresh;
  try {
    sessionStorage.setItem('argos_session_id', fresh);
  } catch {
    /* ignore */
  }
  return fresh;
}

async function flush(): Promise<void> {
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  try {
    await apiClient.post('/flywheel/events', { events: batch });
  } catch {
    // Never throw from analytics — drop the batch rather than disrupt UX.
  }
}

function scheduleFlush(): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

export function trackEvent(event: Omit<FlywheelEvent, 'session_id'>): void {
  buffer.push({ ...event, session_id: getSessionId() });
  if (buffer.length >= MAX_BUFFER) {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    void flush();
  } else {
    scheduleFlush();
  }
}

export function trackPageView(
  product: string,
  path: string,
  context?: Record<string, unknown>,
): void {
  trackEvent({
    event_type: 'page_view',
    product,
    entity_type: 'page',
    entity_id: path,
    context,
  });
}

export function trackEntityClick(
  entityType: string,
  entityId: string,
  context?: Record<string, unknown>,
): void {
  trackEvent({
    event_type: 'entity_clicked',
    entity_type: entityType,
    entity_id: entityId,
    context,
  });
}

export function trackFilterApplied(
  product: string,
  filters: Record<string, unknown>,
): void {
  trackEvent({
    event_type: 'filter_applied',
    product,
    context: { filters },
  });
}

export async function submitFeedback(payload: {
  target_type: string;
  target_id: string;
  rating: -1 | 0 | 1;
  reason_code?: string;
  reason_text?: string;
  suggested_correction?: string;
  context?: Record<string, unknown>;
}): Promise<void> {
  try {
    await apiClient.post('/flywheel/feedback', payload);
  } catch {
    /* ignore */
  }
}

export async function submitOverride(payload: {
  product: string;
  override_type: string;
  original_value: unknown;
  override_value: unknown;
  prediction_id?: string;
  site_id?: string;
  reason_code?: string;
  reason_text?: string;
}): Promise<void> {
  try {
    await apiClient.post('/flywheel/overrides', payload);
  } catch {
    /* ignore */
  }
}

if (typeof window !== 'undefined') {
  const syncFlush = () => {
    if (buffer.length === 0) return;
    const url = `${apiClient.defaults.baseURL ?? '/api/v1'}/flywheel/events`;
    const payload = JSON.stringify({ events: buffer });
    buffer = [];
    try {
      if ('sendBeacon' in navigator) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      }
    } catch {
      /* ignore */
    }
  };
  window.addEventListener('pagehide', syncFlush);
  window.addEventListener('beforeunload', syncFlush);
}
